import { Position, api } from '@common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

// Interface typing the new position payload with explicit requirements and benefits
type NewPositionInput = {
  id?: string;
  title: string;
  location: string;
  type: string;
  department: string;
  salaryRange: string;
  featured: boolean;
  datePosted: string;
  description: string;
  responsibilities?: string[];
  requirements?: string[];
  benefits?: string[];
  additionalInfo?: string;
  seasonalPeriod?: string;
  contractPeriod?: string;
};

export const useJobPositions = () => {
  const queryClient = useQueryClient();

  // Fetch every job position
  const jobPositions = useQuery<Position[]>({
    queryKey: ['jobPositions'],
    queryFn: () => {
      return api.get('/job-positions')
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error retrieving job positions');
          throw error;
        });
    }
  });

  // Fetch a specific position by ID
  const getJobPosition = (id: string) => {
    return useQuery<Position>({
      queryKey: ['jobPosition', id],
      queryFn: () => {
        return api.get(`/job-positions/${id}`)
          .then((response) => response.data)
          .catch((error) => {
            message.error('Error retrieving position details');
            throw error;
          });
      },
      enabled: !!id
    });
  };

  // Fetch similar positions based on the department, excluding the current one
  const getSimilarPositions = (currentPositionId: string, limit: number = 3) => {
    return useQuery<Position[]>({
      queryKey: ['similarPositions', currentPositionId],
      queryFn: async () => {
        const currentPosition = await api.get(`/job-positions/${currentPositionId}`)
          .then(response => response.data)
          .catch(error => {
            console.error('Error retrieving current position:', error);
            throw error;
          });

        // Then get every position
        const allPositions = await api.get('/job-positions')
          .then(response => response.data)
          .catch(error => {
            console.error('Error retrieving all positions:', error);
            throw error;
          });

        const similarPositions = allPositions
          .filter((position: Position) =>
            position.department === currentPosition.department &&
            position.id !== currentPositionId
          )
          .slice(0, limit);

        if (similarPositions.length === 0) {
          return allPositions
            .filter((position: Position) => position.id !== currentPositionId)
            .slice(0, limit);
        }

        return similarPositions;
      },
      enabled: !!currentPositionId
    });
  };

  const addJobPosition = useMutation({
    mutationFn: (newPosition: NewPositionInput) => {


      console.log("Creating new position:", newPosition);

      return api.post('/job-positions', newPosition)
        .then((response) => response.data)
        .catch((error) => {
          console.error('API error adding job position:', error);
          message.error('Error adding job position');
          throw error;
        });
    },
    onSuccess: (data) => {
      // Invalidar e refetch imediatamente as queries
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
      queryClient.refetchQueries({ queryKey: ['jobPositions'] });
      return data; // Return the data so the component can use it
    }
  });

  const updateJobPosition = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Position> }) => {
      // Include the ID in the request body as well
      const updatedData = {
        id: id,  // Adiciona o ID explicitamente no corpo
        ...data
      };

      console.log("Sending data for update:", updatedData);

      return api.patch(`/job-positions/${id}`, updatedData)
        .then((response) => response.data)
        .catch((error) => {
          console.error('API error updating job position:', error);
          message.error('Error updating job position');
          throw error;
        });
    },
    onSuccess: (_data, variables) => {
      // Invalidar e refetch imediatamente as queries
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
      queryClient.refetchQueries({ queryKey: ['jobPositions'] });
      queryClient.invalidateQueries({ queryKey: ['jobPosition', variables.id] });
      queryClient.refetchQueries({ queryKey: ['jobPosition', variables.id] });
    }
  });

  const removeJobPosition = useMutation({
    mutationFn: (positionId: string) => {
      return api.delete(`/job-positions/${positionId}`)
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error removing job position');
          throw error;
        });
    },
    onSuccess: (_, positionId) => {
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
      queryClient.invalidateQueries({ queryKey: ['jobPosition', positionId] });
    }
  });

  const softDeleteJobPosition = useMutation({
    mutationFn: (positionId: string) => {
      return api.patch(`/job-positions/${positionId}/soft-delete`)
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error deleting position');
          throw error;
        });
    },
    onSuccess: (_, positionId) => {
      message.success('Position successfully deleted!');
      queryClient.invalidateQueries({ queryKey: ['jobPositions'] });
      queryClient.invalidateQueries({ queryKey: ['jobPosition', positionId] });
    }
  });

  return {
    jobPositions,
    getJobPosition,
    getSimilarPositions,
    addJobPosition,
    updateJobPosition,
    removeJobPosition,
    softDeleteJobPosition
  };
};
